/**
 * Result type tabs for the search results page.
 *
 * Each tab maps to a value of the 'type' search parameter. Selecting a tab re-renders the
 * results with the Section Rendering API, so the page is never reloaded.
 *
 * Usage:
 *  <search-tabs data-section-id="{{ section.id }}">
 *    <div role="tablist">
 *      <button role="tab" data-types="product" aria-controls="search-results-panel">Products</button>
 *    </div>
 *    <div id="search-results-panel" role="tabpanel">...</div>
 *  </search-tabs>
 */
if (!customElements.get('search-tabs')) {
  class SearchTabs extends HTMLElement {
    connectedCallback() {
      this.tabList = this.querySelector('[role="tablist"]');
      this.panel = this.querySelector('[role="tabpanel"]');
      if (!this.tabList || !this.panel) return;

      this.tabs = Array.from(this.tabList.querySelectorAll('[role="tab"]'));
      this.activeTab = this.tabList.querySelector('[aria-selected="true"]') || this.tabs[0];

      // The tab matching the URL the page was loaded with, used when navigating back to it.
      this.initialTab = this.activeTab;
      this.requestId = 0;

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      this.tabList.addEventListener('click', this.handleClick.bind(this));
      this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);
    }

    /**
     * Handles 'click' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const tab = evt.target.closest('[role="tab"]');
      if (!tab || tab === this.activeTab) return;
      this.selectTab(tab);
    }

    /**
     * Handles 'keydown' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
        case 'ArrowRight': {
          evt.preventDefault();
          const step = evt.key === 'ArrowRight' ? 1 : -1;
          const index = this.tabs.indexOf(this.activeTab) + step;
          this.selectTab(this.tabs[(index + this.tabs.length) % this.tabs.length]);
          break;
        }

        case 'Home':
          evt.preventDefault();
          this.selectTab(this.tabs[0]);
          break;

        case 'End':
          evt.preventDefault();
          this.selectTab(this.tabs[this.tabs.length - 1]);
          break;

        default:
          break;
      }
    }

    /**
     * Handles history changes (e.g. back button clicked).
     */
    handleHistoryChange() {
      const types = new URLSearchParams(window.location.search).get('type');
      let tab = this.initialTab;

      if (types) {
        tab = this.tabs.find(
          (el) => SearchTabs.sortTypes(el.dataset.types) === SearchTabs.sortTypes(types)
        );
      }

      // The result types aren't represented by a tab, so the page has to be rendered again.
      if (!tab) {
        window.location.reload();
        return;
      }

      if (tab !== this.activeTab) this.selectTab(tab, false);
    }

    /**
     * Selects a tab and loads its results.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} [updateUrl=true] - Add the selected result types to the url.
     */
    selectTab(tab, updateUrl = true) {
      if (!tab || tab === this.activeTab) return;

      const url = new URL(window.location.href);
      url.searchParams.set('type', tab.dataset.types);
      url.searchParams.delete('page');

      this.tabs.forEach((el) => {
        const isActive = el === tab;
        el.setAttribute('aria-selected', isActive);

        if (isActive) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });

      this.activeTab = tab;
      this.panel.setAttribute('aria-labelledby', tab.id);

      if (document.activeElement?.matches('[role="tab"]')) tab.focus();

      if (updateUrl) {
        // 'searchParams' is also read by the facet filters when navigating through the history.
        window.history.pushState(
          { searchParams: url.searchParams.toString() },
          '',
          url.toString()
        );
      }

      this.loadResults(url);
    }

    /**
     * Fetches the results for the selected tab and replaces the visible results with them.
     * @param {URL} url - Url of the results to load.
     */
    async loadResults(url) {
      this.requestId += 1;
      const requestId = this.requestId;

      // Cancel the current fetch request. (Raises an exception)
      if (this.abortController) this.abortController.abort('Request changed');
      this.abortController = new AbortController();

      // Disable infinite scrolling whilst the results are being replaced.
      const pagination = this.panel.querySelector('custom-pagination');
      if (pagination) pagination.dataset.pauseInfiniteScroll = 'true';

      this.panel.classList.add('is-loading');
      this.panel.setAttribute('aria-busy', 'true');

      try {
        const fetchUrl = new URL(url);
        fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        // Scripts don't run when inserted as markup, so load them separately.
        this.loadAssets(tmpl.content);

        const newPanel = tmpl.content.getElementById(this.panel.id);
        if (!newPanel) throw new Error('No search results found in the response.');

        this.panel.innerHTML = newPanel.innerHTML;
        this.scrollIntoViewIfNeeded();

        // Broadcast the update for anything else to hook into.
        document.dispatchEvent(new CustomEvent('on:search-tabs:updated', { bubbles: true }));
      } catch (error) {
        console.warn(error); // eslint-disable-line no-console
      } finally {
        if (requestId === this.requestId) {
          this.panel.classList.remove('is-loading');
          this.panel.removeAttribute('aria-busy');
        }
      }
    }

    /**
     * Scrolls to the results if they've been scrolled past.
     */
    scrollIntoViewIfNeeded() {
      const { top } = this.getBoundingClientRect();
      if (top >= 0) return;

      // If the header is sticky, compensate for it when scrolling.
      let headerHeight = 0;
      if (document.querySelector('store-header[data-is-sticky="true"]')) {
        headerHeight = Number.parseInt(
          getComputedStyle(this).getPropertyValue('--header-height').replace('px', ''),
          10
        ) || 0;
      }

      window.scrollTo({
        top: top + window.scrollY - headerHeight - 45,
        behavior: 'smooth'
      });
    }

    /**
     * Loads the stylesheets and scripts of the fetched markup that aren't on the page yet,
     * then removes them so they're not duplicated in the results.
     *
     * A stylesheet stops applying when its link is removed, so the ones inside the results are
     * moved to the head to survive the results being replaced. Scripts stay loaded once they've
     * run, so only their tags are removed.
     * @param {DocumentFragment} fragment - Fetched section markup.
     */
    loadAssets(fragment) {
      const loadedStyles = new Set(
        Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
          .filter((link) => !this.panel.contains(link))
          .map((link) => link.href)
      );
      const loadedScripts = new Set(
        Array.from(document.querySelectorAll('script[src]')).map((script) => script.src)
      );

      fragment.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
        const href = new URL(link.getAttribute('href'), document.baseURI).href;

        if (!loadedStyles.has(href)) {
          loadedStyles.add(href);
          document.head.appendChild(link.cloneNode(true));
        }

        link.remove();
      });

      fragment.querySelectorAll('script[src]').forEach((script) => {
        const src = new URL(script.getAttribute('src'), document.baseURI).href;

        if (!loadedScripts.has(src)) {
          loadedScripts.add(src);

          const newScript = document.createElement('script');
          newScript.src = src;
          newScript.defer = true;
          document.head.appendChild(newScript);
        }

        script.remove();
      });
    }

    /**
     * Puts a list of result types in a consistent order, so two lists can be compared.
     * @param {string} types - Comma separated result types.
     * @returns {string}
     */
    static sortTypes(types) {
      return types.split(',').map((type) => type.trim()).sort().join(',');
    }
  }

  customElements.define('search-tabs', SearchTabs);
}
